import React, { useEffect, useState } from 'react';
import { Check, Info, ChevronDown, FileText, Plane, Home, DollarSign, Shield } from 'lucide-react';
import { pricingCategories as fallbackCategories } from '../mock';
import { Button } from './ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Pricing = () => {
  // Live pricing categories — fetched from /api/price-list. Falls back to
  // hardcoded mock data if the API is unreachable so the page never blanks.
  const [pricingCategories, setPricingCategories] = useState(fallbackCategories);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/price-list`);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        // Map API shape → the shape Pricing.jsx already renders.
        const mapped = (data.categories || []).map((c, i) => ({
          id: i + 1,
          category: c.category,
          services: c.services,
        }));
        if (mapped.length) setPricingCategories(mapped);
      } catch (_) { /* keep fallback */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Category-specific styling
  const getCategoryStyle = (categoryName) => {
    const styles = {
      "Consultation & Documentation": {
        gradient: "from-blue-500/10 to-cyan-500/10",
        border: "border-blue-500/20",
        icon: FileText,
        iconColor: "text-blue-600",
        accentColor: "bg-blue-500"
      },
      "Travel & Logistics": {
        gradient: "from-purple-500/10 to-pink-500/10",
        border: "border-purple-500/20",
        icon: Plane,
        iconColor: "text-purple-600",
        accentColor: "bg-purple-500"
      },
      "Accommodation Services": {
        gradient: "from-green-500/10 to-emerald-500/10",
        border: "border-green-500/20",
        icon: Home,
        iconColor: "text-green-600",
        accentColor: "bg-green-500"
      }
    };
    return styles[categoryName] || styles["Consultation & Documentation"];
  };

  return (
    <section id="pricing" className="py-20 bg-gradient-to-b from-muted/30 to-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-primary/10 px-4 py-2 rounded-full mb-6">
            <DollarSign className="w-5 h-5 text-primary" />
            <span className="text-sm font-semibold text-primary uppercase tracking-wide">Transparent Pricing</span>
          </div>
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
            Clear, Honest Pricing
          </h2>
          <p className="text-base sm:text-lg text-foreground/70 mb-8">
            Clear, detailed pricing for every service. All prices are in Cayman Islands Dollars (CI$) and subject to change based on government rates.
          </p>
          
          {/* Key Pricing Highlights */}
          <div className="grid md:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 border-2 border-primary/20 rounded-xl px-6 py-6 shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Shield className="w-6 h-6 text-primary" />
                <p className="text-xl font-bold text-primary">Initial Consultation</p>
              </div>
              <p className="text-3xl font-bold text-foreground mb-2">CI$50</p>
              <p className="text-base text-foreground/70 leading-relaxed">
                We assess your pet's travel needs, destination requirements, and create a customized travel plan. Our seasoned consultants ensure every detail is covered.
              </p>
              <p className="text-sm text-foreground/50 mt-3 italic">Up to 30 minutes</p>
            </div>
            <div className="bg-gradient-to-br from-accent/10 to-accent/5 border-2 border-accent/20 rounded-xl px-6 py-6 shadow-sm hover:shadow-md transition-shadow text-center">
              <div className="flex items-center justify-center gap-2 mb-3">
                <FileText className="w-6 h-6 text-accent" />
                <p className="text-xl font-bold text-accent">Bespoke Quotes</p>
              </div>
              <p className="text-base text-foreground/80 mt-2 leading-relaxed">
                Personalized quotations tailored to your pet's unique needs and travel requirements
              </p>
            </div>
          </div>
        </div>

        {/* Pricing Categories - Enhanced Visual Hierarchy */}
        <div className="max-w-5xl mx-auto space-y-8">
          {pricingCategories.map((category) => {
            const style = getCategoryStyle(category.category);
            const CategoryIcon = style.icon;
            
            return (
              <div key={category.id} className={`bg-white rounded-2xl shadow-lg overflow-hidden border-2 ${style.border} hover:shadow-xl transition-shadow duration-300`}>
                {/* Category Header with Icon */}
                <div className={`bg-gradient-to-r ${style.gradient} px-6 py-6 border-b-2 ${style.border}`}>
                  <div className="flex items-center gap-4">
                    <div className={`${style.accentColor} p-3 rounded-xl shadow-md`}>
                      <CategoryIcon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl sm:text-3xl font-bold text-foreground">
                        {category.category}
                      </h3>
                      <p className="text-sm text-foreground/60 mt-1">
                        {category.services.length} services available
                      </p>
                    </div>
                  </div>
                </div>

              {/* Services Accordion */}
              <Accordion type="single" collapsible className="w-full">
                {category.services.map((service, index) => (
                  <AccordionItem key={index} value={`item-${index}`} className="border-b border-border last:border-0">
                    <AccordionTrigger className="px-6 py-5 hover:bg-muted/10 transition-all duration-200 group">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between text-left flex-1 pr-4 gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <div className={`w-1.5 h-1.5 rounded-full ${style.accentColor}`}></div>
                            <h4 className="font-bold text-foreground text-lg group-hover:text-primary transition-colors">
                              {service.name}
                            </h4>
                          </div>
                          <p className="text-sm text-foreground/60 leading-relaxed">
                            {service.description}
                          </p>
                        </div>
                        <div className="flex-shrink-0">
                          <div className={`inline-flex items-center gap-2 bg-gradient-to-r ${style.gradient} ${style.border} border px-4 py-2 rounded-lg`}>
                            <div>
                              <div className={`text-base font-bold ${style.iconColor}`}>
                                {service.pricing.firstPet || service.pricing.base}
                              </div>
                              {service.pricing.firstAdditional && (
                                <div className="text-xs text-foreground/60">
                                  {service.pricing.firstAdditional} (2nd pet)
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-6">
                      <div className={`bg-gradient-to-br ${style.gradient} rounded-xl p-6 space-y-4 border ${style.border}`}>
                        {/* Pricing Details */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 text-sm font-bold text-foreground uppercase tracking-wide">
                            Pricing Breakdown
                          </div>
                          <div className="grid gap-3 text-sm bg-white/50 rounded-lg p-4">
                            {service.pricing.firstPet && (
                              <div className="flex justify-between items-center py-2 border-b border-border/30">
                                <span className="text-foreground/70 font-medium">1st Pet:</span>
                                <span className="font-bold text-foreground text-lg">{service.pricing.firstPet}</span>
                              </div>
                            )}
                            {service.pricing.base && !service.pricing.firstPet && (
                              <div className="flex justify-between items-center py-2 border-b border-border/30">
                                <span className="text-foreground/70 font-medium">Price:</span>
                                <span className="font-bold text-foreground text-lg">{service.pricing.base}</span>
                              </div>
                            )}
                            {service.pricing.additional && (
                              <div className="flex justify-between items-center py-2 border-b border-border/30">
                                <span className="text-foreground/70 font-medium">Additional:</span>
                                <span className="font-semibold text-foreground">{service.pricing.additional}</span>
                              </div>
                            )}
                            {service.pricing.homeVisit && (
                              <div className="flex justify-between items-center py-2 border-b border-border/30">
                                <span className="text-foreground/70 font-medium">Home Visit:</span>
                                <span className="font-semibold text-foreground">{service.pricing.homeVisit}</span>
                              </div>
                            )}
                            {service.pricing.firstAdditional && (
                              <div className="flex justify-between items-center py-2 border-b border-border/30">
                                <span className="text-foreground/70 font-medium">2nd Pet:</span>
                                <span className="font-semibold text-foreground">{service.pricing.firstAdditional}</span>
                              </div>
                            )}
                            {service.pricing.secondAdditional && (
                              <div className="flex justify-between items-center py-2">
                                <span className="text-foreground/70 font-medium">3rd Pet:</span>
                                <span className="font-semibold text-foreground">{service.pricing.secondAdditional}</span>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* What's Included */}
                        {service.includes && service.includes.length > 0 && (
                          <div className="space-y-3 pt-4 border-t-2 border-border/30">
                            <div className="flex items-center gap-2 text-sm font-bold text-foreground uppercase tracking-wide">
                              <Check className="w-4 h-4" />
                              What's Included
                            </div>
                            <ul className="space-y-2 bg-white/50 rounded-lg p-4">
                              {service.includes.map((item, idx) => (
                                <li key={idx} className="flex items-start gap-3 text-sm text-foreground/80">
                                  <div className={`${style.accentColor} rounded-full p-0.5 mt-1`}>
                                    <Check className="w-3 h-3 text-white" />
                                  </div>
                                  <span className="leading-relaxed">{item}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Bundle-available callout (orange) */}
                        {service.bundleNote && (
                          <div className="flex items-start gap-3 p-4 bg-orange-50 border border-orange-300 rounded-lg mb-3" data-testid="bundle-available-note">
                            <Info className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-orange-700 font-semibold leading-relaxed">{service.bundleNote}</span>
                          </div>
                        )}

                        {/* Note */}
                        {service.note && (
                          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-amber-900 leading-relaxed">{service.note}</span>
                          </div>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
            );
          })}
        </div>

        {/* Bottom CTA - Enhanced */}
        <div className="mt-16 text-center max-w-3xl mx-auto">
          <div className="bg-gradient-to-br from-primary/10 via-accent/10 to-primary/10 rounded-2xl p-8 sm:p-10 border-2 border-primary/20 shadow-xl">
            <div className="inline-flex items-center gap-2 bg-primary/20 px-4 py-2 rounded-full mb-4">
              <Shield className="w-4 h-4 text-primary" />
              <span className="text-xs font-bold text-primary uppercase tracking-wide">Transparent Process</span>
            </div>
            <h3 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">
              Ready to Get Started?
            </h3>
            <p className="text-base sm:text-lg text-foreground/70 mb-8 leading-relaxed">
              Schedule your initial consultation (CI$50) to discuss your pet's travel needs. We'll then prepare a bespoke quotation tailored specifically to your requirements, ensuring transparent pricing for your unique situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                className="bg-primary hover:bg-primary/90 text-white shadow-lg hover:shadow-xl transition-all"
              >
                <Shield className="w-5 h-5 mr-2" />
                Schedule Your Consultation
              </Button>
              <Button 
                onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                size="lg"
                variant="outline"
                className="border-2 border-primary text-primary hover:bg-primary/5"
              >
                Learn More About Our Services
              </Button>
            </div>
            <p className="text-sm text-foreground/50 mt-6">
              💚 No obligation • Transparent pricing • 12+ years of expertise
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};
